import JSZip from "jszip";
import {
    BdDocType, BdDocument, BdFigureSpec, BdMaskingView, BdProposal, BdReply, BdSectionTemplate, BdShare, CHECK_STATUS, DOC_TYPES, fillTemplate, SEVERITY,
} from "../../types/bd/Bd";
import { guideStatus, hexColor, SAFE_LINK, styleValue } from "./bdProposalUtils";
import { rasterizeFigure } from "./researchExport";
import { getReq } from "../request";
import { isOpenPoint } from "./bdShare";
import { pickOf } from "./bdPicks";

/**
 * A document with comments and suggested changes, built in the browser and
 * written as Word (.docx: comments in the margin, tracked changes the other
 * side can accept or reject — Google Docs shows them as suggestions) or as a
 * web page with the comments beside their words.
 */

export type ParaStyle = "Title" | "Subtitle" | "Heading1" | "Heading2" | "Heading3" | "Heading4" | "Normal" | "ListBullet" | "ListNumber" | "Quote" | "Figure";

export interface DocAnn { s: number; e: number; cid: number | null; repl: string | null; }

/** Inline formatting beyond bold (from a proposal section): colours are #RRGGBB, links http(s) or mailto. */
export interface RunFormat { i?: boolean; u?: boolean; s?: boolean; color?: string; bg?: string; href?: string; }

/** A table read from a proposal section: rows of cells (each cell holds paragraphs), merged cells as a span. */
export interface DocGrid { rows: Array<{ head: boolean; cells: Array<{ span: number; paras: DocPara[] }> }>; }

export interface DocPara {
    style: ParaStyle;
    text: string;
    bold: Array<[number, number]>;
    anns: DocAnn[];
    /** Comment on the whole paragraph. */
    cid?: number | null;
    cids?: number[];
    /** A suggested new paragraph (tracked insertion). */
    inserted?: boolean;
    /** A picture: its id (the SVG id the download window draws a chart or diagram under) and, for our own upload, the API path of its image. */
    figure?: { svgId: string; spec: BdFigureSpec; imagePath?: string };
    /** A table instead of text: cells already written as text; numeric columns sit right. */
    table?: { columns: string[]; rows: string[][]; numeric: boolean[] };
    /** Italic, underline, strike, colours and links over [s, e) of the text. */
    fmt?: Array<{ s: number; e: number; f: RunFormat }>;
    align?: "center" | "right" | "justify";
    /** A list item: its list (each list numbers from 1), its level (0 = top) and, in a numbered list, its number. */
    list?: { id: number; level: number; n: number };
    /** A page break instead of text. */
    pageBreak?: boolean;
    /** A table from a proposal section instead of text. */
    grid?: DocGrid;
}

/** Every paragraph in reading order, table cells included. */
export const flatParas = (paras: DocPara[]): DocPara[] =>
    paras.flatMap((p) => (p.grid ? [p, ...p.grid.rows.flatMap((r) => r.cells.flatMap((c) => flatParas(c.paras)))] : [p]));

export interface DocComment {
    id: number;
    /** Stable across rebuilds: author|text#n — the key edits and deletes refer to. */
    key: string;
    text: string;
    orig: string;
    edited: boolean;
    author: string;
    initials: string;
}

export interface DocModel {
    title: string;
    paras: DocPara[];
    comments: DocComment[];
    deleted: Array<{ key: string; text: string; author: string }>;
    /** Share item id → comment id (send version). */
    shareCid: Record<string, number | null>;
    /** Written into the file so a returned copy finds its way back. */
    docId?: string;
    keyCount: Record<string, number>;
}

/** A person's changes to the comments before downloading. */
export interface CommentEdits {
    edits: Record<string, string>;
    deleted: string[];
    added: Array<{ q: string; text: string }>;
}

export const NO_EDITS: CommentEdits = { edits: {}, deleted: [], added: [] };

/** A picture drawn to a PNG; size in CSS pixels. */
export interface FigureImage { dataUrl: string; width: number; height: number; }

/** A picture's size in pixels, or null when it won't load. */
const imageSize = (dataUrl: string): Promise<{ width: number; height: number } | null> => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
});

/** Every picture in the model as an image: charts and diagrams drawn from their SVGs (on screen in the download window), our uploads fetched. */
export async function figureImages(m: DocModel): Promise<Record<string, FigureImage>> {
    const out: Record<string, FigureImage> = {};
    for (const p of m.paras) {
        if (!p.figure || out[p.figure.svgId]) continue;
        if (p.figure.imagePath) {
            const response = await getReq(p.figure.imagePath);
            const dataUrl: string | null = response.status === 200 ? response.body?.dataUrl ?? null : null;
            const size = dataUrl ? await imageSize(dataUrl) : null;
            if (dataUrl && size) out[p.figure.svgId] = { dataUrl, ...size };
            continue;
        }
        const drawn = await rasterizeFigure(p.figure.svgId);
        if (drawn) out[p.figure.svgId] = { dataUrl: drawn.canvas.toDataURL("image/png"), width: drawn.width, height: drawn.height };
    }
    return out;
}

export function newModel(title: string): DocModel {
    return { title, paras: [], comments: [], deleted: [], shareCid: {}, keyCount: {} };
}

const initialsOf = (a: string): string => (a === "CRX analysis (AI)" ? "AI" : a.split(" ").map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase());

/** Add a comment (unless the person deleted it); their edit replaces the text. */
export function addComment(m: DocModel, text: string, author: string, edits: CommentEdits): number | null {
    const base = `${author}|${text}`;
    m.keyCount[base] = (m.keyCount[base] ?? 0) + 1;
    const key = `${base}#${m.keyCount[base]}`;
    if (edits.deleted.includes(key)) { m.deleted.push({ key, text, author }); return null; }
    const id = m.comments.length;
    const edited = edits.edits[key];
    m.comments.push({ id, key, text: edited ?? text, orig: text, edited: edited !== undefined && edited !== text, author, initials: initialsOf(author) });
    return id;
}

export function para(m: DocModel, style: ParaStyle, text: string, extra: Partial<DocPara> = {}): DocPara {
    const p: DocPara = { style, text, bold: [], anns: [], ...extra };
    m.paras.push(p);
    return p;
}

/** Put a comment (and optionally a replacement) on the first free occurrence of the words. */
export function annotate(m: DocModel, quote: string, cid: number | null, repl: string | null = null): boolean {
    if (!quote) return false;
    for (const p of flatParas(m.paras)) {
        if (p.style === "Title" || p.style === "Subtitle") continue;
        let i = p.text.indexOf(quote);
        while (i >= 0) {
            const s = i;
            const e = i + quote.length;
            if (!p.anns.some((a) => s < a.e && e > a.s)) { p.anns.push({ s, e, cid, repl }); return true; }
            i = p.text.indexOf(quote, e);
        }
    }
    return false;
}

/** Comment on the words if found, else on the paragraph holding them, else at the top. */
export function attach(m: DocModel, quote: string, cid: number | null, repl: string | null = null): void {
    if (cid === null && repl === null) return;
    if (quote && annotate(m, quote, cid, repl)) return;
    if (cid === null) return;
    const p = (quote && flatParas(m.paras).find((pp) => pp.style !== "Title" && pp.style !== "Subtitle" && pp.text.includes(quote))) || m.paras[0];
    p.cids = [...(p.cids ?? []), cid];
}

export interface DocRun { t: string; bold: boolean; ann?: DocAnn; start?: boolean; end?: boolean; f?: RunFormat; }

/** Split a paragraph into runs at every bold / format / comment boundary. */
export function runsOf(p: DocPara): DocRun[] {
    const cuts = new Set<number>([0, p.text.length]);
    p.bold.forEach(([s, e]) => { cuts.add(s); cuts.add(e); });
    (p.fmt ?? []).forEach((x) => { cuts.add(x.s); cuts.add(x.e); });
    p.anns.forEach((a) => { cuts.add(a.s); cuts.add(a.e); });
    const pts = Array.from(cuts).filter((x) => x >= 0 && x <= p.text.length).sort((a, b) => a - b);
    const out: DocRun[] = [];
    for (let k = 0; k < pts.length - 1; k++) {
        const a = pts[k], b = pts[k + 1];
        const ann = p.anns.find((x) => a >= x.s && b <= x.e);
        const fs = (p.fmt ?? []).filter((x) => a >= x.s && b <= x.e);
        out.push({ t: p.text.slice(a, b), bold: p.bold.some(([s, e]) => a >= s && b <= e), ann, start: !!ann && ann.s === a, end: !!ann && ann.e === b, ...(fs.length ? { f: Object.assign({}, ...fs.map((x) => x.f)) } : {}) });
    }
    if (!out.length) out.push({ t: "", bold: false });
    return out;
}

/** Number comments in reading order, as Word does. */
export function renumber(m: DocModel): DocModel {
    const order: number[] = [];
    const seen = new Set<number>();
    const push = (id: number | null | undefined) => { if (id !== null && id !== undefined && !seen.has(id)) { seen.add(id); order.push(id); } };
    const all = flatParas(m.paras);
    all.forEach((p) => { [p.cid, ...(p.cids ?? [])].forEach(push); [...p.anns].sort((x, y) => x.s - y.s).forEach((x) => push(x.cid)); });
    m.comments.forEach((c) => push(c.id));
    const map = new Map(order.map((old, i) => [old, i]));
    const f = (id: number | null | undefined): number | null => (id === null || id === undefined ? null : map.get(id) ?? null);
    m.comments = order.map((old, i) => ({ ...m.comments[old], id: i }));
    all.forEach((p) => {
        if (p.cid !== undefined && p.cid !== null) p.cid = f(p.cid);
        if (p.cids) p.cids = p.cids.map((x) => f(x)).filter((x): x is number => x !== null);
        p.anns.forEach((x) => { x.cid = f(x.cid); });
    });
    Object.keys(m.shareCid).forEach((k) => { m.shareCid[k] = f(m.shareCid[k]); });
    return m;
}

/** The person's own comments, added last. */
export function addPersonComments(m: DocModel, added: CommentEdits["added"], author: string, edits: CommentEdits): void {
    added.forEach((a) => attach(m, a.q, addComment(m, a.text, author, edits)));
}

const textParas = (m: DocModel, text: string) => text.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean).forEach((x) => para(m, "Normal", x));

// ── builders: analysis ───────────────────────────────────────────────────

export interface AnalysisExportOptions {
    doc: BdDocument;
    /** internal = everything for our team; send = only the Share back points, as written. */
    preset: "internal" | "send";
    /** masking = the original with a note on every masked item; masked = exactly what the AI received. */
    version: "original" | "masking" | "masked";
    comments: boolean;
    suggestions: boolean;
    summary: boolean;
    /** Send version: only the points still open after their reply. */
    onlyOpen: boolean;
    share: BdShare | null;
    reply: BdReply | null;
    masking: BdMaskingView | null;
    people: Record<number, string>;
    ourName: string;
    today: string;
    edits: CommentEdits;
    /** From BD settings ({our}, {date}). */
    subtitles?: { share: string; followUp: string };
}

const fmt = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export function buildAnalysisModel(o: AnalysisExportOptions): DocModel {
    const d = o.doc;
    const r = d.results ?? null;
    const text = d.editedText ?? d.originalText ?? "";
    const from = d.counterparty || "the sender";
    const typeName = DOC_TYPES[d.docType as BdDocType]?.label ?? "Document";
    const author = (who: "ai" | "us" | "masking") => (who === "ai" ? "CRX analysis (AI)" : who === "masking" ? "CRX masking" : o.ourName);

    if (o.version === "masked") {
        const m = newModel(`${d.title} (masked copy)`);
        para(m, "Title", d.title);
        para(m, "Subtitle", `${from} · ${typeName} · Masked copy: exactly what the AI received`);
        textParas(m, o.masking?.masked ?? d.maskedText ?? "");
        return m;
    }

    if (o.version === "masking") {
        const m = newModel(`${d.title} — masking notes`);
        para(m, "Title", d.title);
        para(m, "Subtitle", `${from} · ${typeName} · Exported from CRX ${fmt(o.today)}`);
        textParas(m, text);
        if (o.comments && o.masking) {
            o.masking.ranges.forEach((x) => {
                const cid = addComment(m, x.on ? `Masked as ${x.code} · ${x.reason}${x.sure ? "" : " (not sure)"}` : `Shown to the AI · ${x.reason}`, author("masking"), o.edits);
                attach(m, x.text, cid);
            });
        }
        addPersonComments(m, o.edits.added, "You", o.edits);
        return renumber(m);
    }

    if (o.preset === "send") {
        const m = newModel(`${d.title} — comments from ${o.ourName}`);
        m.docId = `crx-bd:${d.documentId}`;
        para(m, "Title", d.title);
        const vars = { our: o.ourName, date: fmt(o.today) };
        para(m, "Subtitle", o.onlyOpen
            ? fillTemplate(o.subtitles?.followUp ?? "Follow-up from {our} · {date} · These points are still open. Please reply to each comment.", vars)
            : fillTemplate(o.subtitles?.share ?? "Comments from {our} · {date} · Please reply to each comment, and accept or reject the suggested changes.", vars));
        textParas(m, text);
        (o.share?.items ?? []).filter((x) => x.inc && (!o.onlyOpen || isOpenPoint(o.reply, x))).forEach((x) => {
            const label = x.kind === "Question" ? "Question" : x.kind === "Correction" ? "Suggested change" : "Comment";
            let cid = o.comments ? addComment(m, `${x.ref} · ${label}: ${x.note}`, o.ourName, o.edits) : null;
            // the reference must survive edits: it's how their answer finds its point
            if (cid !== null && !m.comments[cid].text.trim().startsWith(x.ref)) m.comments[cid].text = `${x.ref} · ${m.comments[cid].text.trim()}`;
            m.shareCid[x.id] = cid;
            const repl = o.suggestions && x.kind === "Correction" ? x.repl : null;
            if (!x.q || !annotate(m, x.q, cid, repl)) { if (cid !== null) { m.paras[0].cids = [...(m.paras[0].cids ?? []), cid]; cid = null; } }
        });
        addPersonComments(m, o.edits.added, o.ourName, o.edits);
        return renumber(m);
    }

    const m = newModel(`${d.title} — CRX analysis`);
    para(m, "Title", d.title);
    para(m, "Subtitle", `${from} · ${typeName} · Exported from CRX ${fmt(o.today)}`);
    textParas(m, text);
    if (r && (o.comments || o.suggestions)) {
        r.gaps.forEach((g) => {
            const owner = g.ownerId ? o.people[g.ownerId] : null;
            const txt = `${SEVERITY[g.sev]?.label ?? "Medium"} priority${g.done ? " · done" : ""}: ${g.t}${g.act ? `\nWhat to do: ${g.act}` : ""}${owner ? `\nWho: ${owner}${g.due ? `, by ${fmt(g.due)}` : ""}` : ""}`;
            const cid = o.comments ? addComment(m, txt, g.mine ? "You" : author("ai"), o.edits) : null;
            attach(m, g.q, cid, o.suggestions && g.fix ? g.fix : null);
        });
        if (o.comments) r.unclear.forEach((u) => attach(m, u.q, addComment(m, `Unclear: ${u.t}`, author("ai"), o.edits)));
    }
    if (r && o.summary) {
        para(m, "Heading1", `CRX analysis · ${fmt(o.today)}`);
        if (r.summary) para(m, "Normal", r.summary);
        para(m, "Heading2", DOC_TYPES[d.docType as BdDocType]?.check ?? "Checklist");
        r.check.forEach((c) => para(m, "ListBullet", `${CHECK_STATUS[c.st].label}${c.ver ? " (confirmed)" : ""} — ${c.t}${c.ev ? `. ${c.ev}` : ""}`));
        para(m, "Heading2", DOC_TYPES[d.docType as BdDocType]?.questions ?? "Questions");
        r.questions.filter((q) => pickOf("question", q, d.docType as BdDocType) === "ask").forEach((q) => para(m, "ListBullet", `[${q.cat}] ${q.t}`));
        para(m, "Heading2", "Documents to prepare or collect");
        r.missing.forEach((x) => para(m, "ListBullet", `${x.done ? "Ready" : "Missing"} — ${x.t}${x.ownerId && o.people[x.ownerId] ? ` (${o.people[x.ownerId]})` : ""}`));
        r.notAttached.forEach((x) => para(m, "ListBullet", `Not attached — ${x}`));
        para(m, "Heading2", "Key dates");
        r.dates.forEach((x) => para(m, "ListBullet", `${x.date ? fmt(x.date) : "Date unknown"} — ${x.label}${x.note ? ` (${x.note})` : ""}`));
    }
    addPersonComments(m, o.edits.added, "You", o.edits);
    return renumber(m);
}

// ── builders: proposal ───────────────────────────────────────────────────

// ── reading a proposal section's HTML (the rich editor's subset, crx-be BdHtml) ──

const HEADING: Record<string, ParaStyle> = { H1: "Heading2", H2: "Heading2", H3: "Heading3", H4: "Heading4", H5: "Heading4", H6: "Heading4" };
const INLINE = /^(STRONG|B|EM|I|U|INS|S|STRIKE|DEL|SPAN|A|BR|SUB|SUP|MARK|FONT|SMALL|BIG|CODE|LABEL)$/;
const BLOCKISH = /^(P|DIV|H[1-6]|BLOCKQUOTE|LI)$/;
const SKIP = /^(SCRIPT|STYLE|FIGURE|IMG|HR|COLGROUP|TEMPLATE)$/;

const alignOf = (el: Element): DocPara["align"] => {
    const a = styleValue(el, "text-align") || (el.getAttribute("align") ?? "").toLowerCase();
    return a === "center" || a === "right" || a === "justify" ? a : undefined;
};

const formatOf = (el: Element, f: RunFormat): RunFormat => {
    const tag = el.nodeName;
    const out = { ...f };
    if (tag === "EM" || tag === "I") out.i = true;
    if (tag === "U" || tag === "INS") out.u = true;
    if (tag === "S" || tag === "STRIKE" || tag === "DEL") out.s = true;
    if (tag === "SPAN" || tag === "MARK" || tag === "FONT") {
        const color = hexColor(styleValue(el, "color") || el.getAttribute("color"));
        const bg = hexColor(styleValue(el, "background-color"));
        if (color) out.color = color;
        if (bg) out.bg = bg;
    }
    if (tag === "A") { const href = (el.getAttribute("href") ?? "").trim(); if (SAFE_LINK.test(href)) out.href = href; }
    return out;
};

/** One paragraph from inline nodes: spaces collapse as a browser shows them; bold and formats are ranges of the text. */
function inlinePara(nodes: Node[], style: ParaStyle, align?: DocPara["align"]): DocPara | null {
    let text = "";
    const bold: Array<[number, number]> = [];
    const fmt: NonNullable<DocPara["fmt"]> = [];
    const newline = () => { text = `${text.replace(/ $/, "")}\n`; };
    const walk = (n: Node, f: RunFormat) => {
        if (n.nodeType === 3) {
            let v = (n.textContent ?? "").replace(/\s+/g, " ");
            if (!text || /[ \n]$/.test(text)) v = v.replace(/^ /, "");
            const s = text.length;
            text += v;
            if (Object.keys(f).length && text.length > s) fmt.push({ s, e: text.length, f });
            return;
        }
        if (n.nodeType !== 1 || SKIP.test(n.nodeName)) return;
        const el = n as Element;
        if (el.nodeName === "BR") { newline(); return; }
        if (BLOCKISH.test(el.nodeName) && text && !text.endsWith("\n")) newline();
        const s = text.length;
        const next = formatOf(el, f);
        el.childNodes.forEach((c) => walk(c, next));
        if ((el.nodeName === "B" || el.nodeName === "STRONG") && text.length > s) bold.push([s, text.length]);
    };
    nodes.forEach((n) => walk(n, {}));
    const lead = text.length - text.replace(/^\s+/, "").length;
    const kept = text.trim();
    if (!kept) return null;
    const cut = <T extends { s: number; e: number }>(r: T): T => ({ ...r, s: Math.max(0, r.s - lead), e: Math.min(kept.length, r.e - lead) });
    return {
        style, text: kept, anns: [],
        bold: bold.map(([s, e]) => cut({ s, e })).filter((r) => r.e > r.s).map((r): [number, number] => [r.s, r.e]),
        ...(fmt.length ? { fmt: fmt.map(cut).filter((r) => r.e > r.s) } : {}),
        ...(align ? { align } : {}),
    };
}

interface ReadCtx { quote: boolean; level: number }

/** A list and the lists inside it; each list gets its own id, so numbering starts at 1 in every list. */
function readList(list: Element, level: number, ids: { n: number }): DocPara[] {
    const id = ++ids.n;
    const style: ParaStyle = list.nodeName === "OL" ? "ListNumber" : "ListBullet";
    const out: DocPara[] = [];
    let n = 0;
    Array.from(list.children).forEach((li) => {
        if (li.nodeName !== "LI") { out.push(...readBlocks(li, { quote: false, level }, ids)); return; }
        const own: Node[] = [];
        const nested: Element[] = [];
        li.childNodes.forEach((c) => { if (c.nodeName === "UL" || c.nodeName === "OL") nested.push(c as Element); else own.push(c); });
        const p = inlinePara(own, style);
        if (p) { n += 1; out.push({ ...p, list: { id, level: Math.min(level, 8), n } }); }
        nested.forEach((x) => out.push(...readList(x, level + 1, ids)));
    });
    return out;
}

function readTable(table: Element, ids: { n: number }): DocPara | null {
    const rows: DocGrid["rows"] = [];
    table.querySelectorAll("tr").forEach((tr) => {
        if (tr.closest("table") !== table) return;
        const cells = Array.from(tr.children).filter((c) => c.nodeName === "TD" || c.nodeName === "TH");
        if (!cells.length) return;
        const head = tr.parentElement?.nodeName === "THEAD" || cells.every((c) => c.nodeName === "TH");
        rows.push({
            head,
            cells: cells.map((c) => {
                const span = Number(c.getAttribute("colspan"));
                return { span: Number.isInteger(span) && span > 1 ? Math.min(span, 20) : 1, paras: readBlocks(c, { quote: false, level: 0 }, ids) };
            }),
        });
    });
    return rows.length ? { style: "Normal", text: "", bold: [], anns: [], grid: { rows } } : null;
}

function readBlocks(root: Node, ctx: ReadCtx, ids: { n: number }): DocPara[] {
    const out: DocPara[] = [];
    let loose: Node[] = [];
    const flush = () => {
        const p = loose.length ? inlinePara(loose, ctx.quote ? "Quote" : "Normal") : null;
        if (p) out.push(p);
        loose = [];
    };
    root.childNodes.forEach((n) => {
        const tag = n.nodeType === 1 ? n.nodeName : "";
        if (!tag || INLINE.test(tag)) { loose.push(n); return; }
        flush();
        const el = n as Element;
        if (tag === "P" || HEADING[tag]) { const p = inlinePara(Array.from(el.childNodes), HEADING[tag] ?? (ctx.quote ? "Quote" : "Normal"), alignOf(el)); if (p) out.push(p); }
        else if (tag === "BLOCKQUOTE") out.push(...readBlocks(el, { ...ctx, quote: true }, ids));
        else if (tag === "UL" || tag === "OL") out.push(...readList(el, ctx.level, ids));
        else if (tag === "TABLE") { const t = readTable(el, ids); if (t) out.push(t); }
        else if (tag === "DIV" && el.classList.contains("page-break")) out.push({ style: "Normal", text: "", bold: [], anns: [], pageBreak: true });
        else if (!SKIP.test(tag)) out.push(...readBlocks(el, ctx, ids));
    });
    flush();
    return out;
}

/**
 * Section HTML → paragraphs: headings 1–3, quotes, bulleted and numbered
 * lists (nested), tables with merged cells and a header row, page breaks;
 * bold, italic, underline, strike, colours, highlight and links inside.
 */
export function htmlToParas(m: DocModel, html: string): void {
    // inert parse: nothing loads or runs
    const body = new DOMParser().parseFromString(`<!doctype html><body>${html || ""}</body>`, "text/html").body;
    const ids = { n: Math.max(0, ...flatParas(m.paras).map((p) => p.list?.id ?? 0)) };
    m.paras.push(...readBlocks(body, { quote: false, level: 0 }, ids));
}

export interface ProposalExportOptions {
    proposal: BdProposal;
    templates: BdSectionTemplate[];
    comments: boolean;
    suggestions: boolean;
    notes: boolean;
    ourName: string;
    today: string;
    edits: CommentEdits;
    /** The proposal template's frame goes on top (utils/bd/bdDocTemplate): no title lines, and plain section names as in the document view. */
    framed?: boolean;
}

export function buildProposalModel(o: ProposalExportOptions): DocModel {
    const p = o.proposal;
    const m = newModel(p.title);
    if (!o.framed) {
        para(m, "Title", p.title);
        para(m, "Subtitle", `${o.ourName}${p.clientName ? ` · for ${p.clientName}` : ""} · ${fmt(o.today)}`);
    }
    (p.sections ?? []).filter((s) => s.include).forEach((s, i) => {
        const head = para(m, "Heading1", o.framed ? s.name : `${i + 1}. ${s.name}`);
        const start = m.paras.length;
        htmlToParas(m, s.html);
        if (m.paras.length === start) para(m, "Normal", "[Empty section]");
        const secParas = m.paras.slice(start);
        (s.figures ?? []).forEach((f) => {
            const fp = para(m, "Figure", "", { figure: { svgId: `bd-dl-${s.id}-${f.id}`, spec: f.spec, ...(f.spec.type === "image" ? { imagePath: `bd/proposals/${p.proposalId}/figures/${f.id}/image` } : {}) } });
            if (o.comments && f.from === "ai") fp.cid = addComment(m, "Drawn by AI from this section: check every label before it goes out.", "CRX analysis (AI)", o.edits);
        });
        if (o.comments) {
            flatParas(secParas).forEach((pp) => {
                const re = /\[CONFIRM:[^\]]*\]/g;
                let mm: RegExpExecArray | null;
                while ((mm = re.exec(pp.text))) {
                    const startAt = mm.index, endAt = mm.index + mm[0].length;
                    if (pp.anns.some((a) => startAt < a.e && endAt > a.s)) continue;
                    pp.anns.push({ s: startAt, e: endAt, cid: addComment(m, `Confirm this fact: ${mm[0].replace(/^\[CONFIRM:\s*|\]$/g, "")}`, "CRX analysis (AI)", o.edits), repl: null });
                }
            });
            if (p.step === "draft" || p.step === "review") {
                const tpl = o.templates.find((t) => t.id === s.id);
                const miss = tpl ? guideStatus(tpl, s.html).filter((x) => !x.ok).map((x) => x.t) : [];
                if (miss.length) head.cid = addComment(m, `This section should also cover: ${miss.join("; ")}.`, "CRX analysis (AI)", o.edits);
            }
            if (o.notes) (p.notes ?? []).filter((n) => n.sec === s.id).forEach((n) => {
                const cid = addComment(m, n.t, n.by || "Team note", o.edits);
                if (cid !== null) head.cids = [...(head.cids ?? []), cid];
            });
        }
        (p.review ?? []).filter((c) => c.sec === s.id && !c.applied && !c.dismissed).forEach((c) => {
            const cid = o.comments ? addComment(m, `${c.kind}: ${c.issue}`, "CRX analysis (AI)", o.edits) : null;
            if (!c.find) {
                if (o.suggestions && c.sug) m.paras.splice(m.paras.indexOf(secParas[secParas.length - 1]) + 1, 0, { style: "Normal", text: c.sug, bold: [], anns: [], inserted: true, cid });
                else if (cid !== null) head.cids = [...(head.cids ?? []), cid];
                return;
            }
            if (annotate(m, c.find, cid, o.suggestions ? c.sug : null)) return;
            if (cid !== null) head.cids = [...(head.cids ?? []), cid];
        });
    });
    addPersonComments(m, o.edits.added, "You", o.edits);
    return renumber(m);
}

// ── writers ──────────────────────────────────────────────────────────────

const xe = (s: string): string => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * The model as a Word file: comments, tracked changes by `${ourName} BD`.
 * @param m - The model.
 * @param reviewer - The tracked-change author ("Crownox BD").
 * @param images - The pictures, drawn (figureImages); one that's missing is named in the text instead.
 */
export function docxZip(m: DocModel, reviewer: string, images: Record<string, FigureImage> = {}): JSZip {
    const date = new Date().toISOString().slice(0, 19) + "Z";
    let rev = 1;
    const W = 'xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"';
    // rPr children in the schema's order: rStyle, b, i, strike, color, u, shd (colours are checked #RRGGBB)
    const rPr = (bold: boolean, f?: RunFormat, link = false): string => {
        const x = `${link ? "<w:rStyle w:val=\"Hyperlink\"/>" : ""}${bold ? "<w:b/>" : ""}${f?.i ? "<w:i/>" : ""}${f?.s ? "<w:strike/>" : ""}${f?.color ? `<w:color w:val="${f.color.slice(1)}"/>` : ""}${f?.u ? "<w:u w:val=\"single\"/>" : ""}${f?.bg ? `<w:shd w:val="clear" w:color="auto" w:fill="${f.bg.slice(1)}"/>` : ""}`;
        return x ? `<w:rPr>${x}</w:rPr>` : "";
    };
    const tRuns = (text: string, bold: boolean, tag = "w:t", f?: RunFormat, link = false) => String(text).split("\n").map((seg, i) => `${i ? "<w:r><w:br/></w:r>" : ""}${seg ? `<w:r>${rPr(bold, f, link)}<${tag} xml:space="preserve">${xe(seg)}</${tag}></w:r>` : ""}`).join("");
    const links: string[] = [];
    const linked = (href: string, runs: string): string => {
        if (!links.includes(href)) links.push(href);
        return `<w:hyperlink r:id="rIdLink${links.indexOf(href) + 1}" w:history="1">${runs}</w:hyperlink>`;
    };
    // numId 1 is the List Bullet style's; every list in a section gets its own, so numbers restart at 1
    const lists = new Map<number, { numId: number; ordered: boolean; level: number }>();
    const numIdOf = (p: DocPara): number => {
        const l = p.list as NonNullable<DocPara["list"]>;
        if (!lists.has(l.id)) lists.set(l.id, { numId: lists.size + 2, ordered: p.style === "ListNumber", level: l.level });
        return (lists.get(l.id) as { numId: number }).numId;
    };
    const ref = (id: number) => `<w:commentRangeEnd w:id="${id}"/><w:r><w:rPr><w:rStyle w:val="CommentReference"/></w:rPr><w:commentReference w:id="${id}"/></w:r>`;
    const media: Array<{ n: number; base64: string; ext: "png" | "jpeg" }> = [];
    const figureRun = (f: NonNullable<DocPara["figure"]>): string => {
        const img = images[f.svgId];
        if (!img) return tRuns(`[Picture: ${f.spec.title}]`, false);
        const n = media.length + 1;
        const base64 = img.dataUrl.split(",")[1] ?? "";
        // the server labels every stored picture image/jpeg, so the file type comes from the bytes (JPEG starts FF D8 FF)
        media.push({ n, base64, ext: base64.startsWith("/9j/") ? "jpeg" : "png" });
        // CSS pixels to EMU (9525 a pixel): no wider than the text (9638 twips × 635) and no taller than 8 inches
        const scale = Math.min(1, (9638 * 635) / (img.width * 9525), (8 * 914400) / (img.height * 9525));
        const cx = Math.round(img.width * 9525 * scale);
        const cy = Math.round(img.height * 9525 * scale);
        return `<w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent cx="${cx}" cy="${cy}"/><wp:docPr id="${n}" name="Picture ${n}" descr="${xe(f.spec.title)}"/><a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:nvPicPr><pic:cNvPr id="${n}" name="image${n}.${media[n - 1].ext}"/><pic:cNvPicPr/></pic:nvPicPr><pic:blipFill><a:blip r:embed="rIdImg${n}"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill><pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r>`;
    };
    const tableXml = (t: NonNullable<DocPara["table"]>): string => {
        const border = ["top", "left", "bottom", "right", "insideH", "insideV"].map((b) => `<w:${b} w:val="single" w:sz="4" w:space="0" w:color="C9D2D3"/>`).join("");
        const tc = (text: string, i: number, head: boolean) => `<w:tc><w:tcPr>${head ? '<w:shd w:val="clear" w:color="auto" w:fill="D9E021"/>' : ""}</w:tcPr><w:p><w:pPr><w:spacing w:after="0"/>${t.numeric[i] && !head ? '<w:jc w:val="right"/>' : ""}</w:pPr>${tRuns(text, head)}</w:p></w:tc>`;
        const tr = (cells: string[], head: boolean) => `<w:tr>${head ? "<w:trPr><w:tblHeader/></w:trPr>" : ""}${cells.map((c, i) => tc(c, i, head)).join("")}</w:tr>`;
        return `<w:tbl><w:tblPr><w:tblW w:w="5000" w:type="pct"/><w:tblBorders>${border}</w:tblBorders><w:tblCellMar><w:left w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tblCellMar></w:tblPr><w:tblGrid>${t.columns.map(() => "<w:gridCol/>").join("")}</w:tblGrid>${tr(t.columns, true)}${t.rows.map((r) => tr(r, false)).join("")}</w:tbl><w:p/>`;
    };
    /** A proposal table: one grid column per cell slot, merged cells as gridSpan, the header row shaded and repeated on each page. */
    const gridXml = (g: DocGrid): string => {
        const width = (r: DocGrid["rows"][number]) => r.cells.reduce((n, c) => n + c.span, 0);
        const cols = Math.max(1, ...g.rows.map(width));
        const border = ["top", "left", "bottom", "right", "insideH", "insideV"].map((b) => `<w:${b} w:val="single" w:sz="4" w:space="0" w:color="C9D2D3"/>`).join("");
        const tc = (span: number, head: boolean, inner: string) => `<w:tc><w:tcPr>${span > 1 ? `<w:gridSpan w:val="${span}"/>` : ""}${head ? "<w:shd w:val=\"clear\" w:color=\"auto\" w:fill=\"F3F5C4\"/>" : ""}</w:tcPr>${inner || "<w:p/>"}</w:tc>`;
        const tr = (r: DocGrid["rows"][number]) => `<w:tr>${r.head ? "<w:trPr><w:tblHeader/></w:trPr>" : ""}${r.cells.map((c) => tc(c.span, r.head, c.paras.map((p) => paraXml(p, r.head)).join(""))).join("")}${width(r) < cols ? tc(cols - width(r), r.head, "") : ""}</w:tr>`;
        return `<w:tbl><w:tblPr><w:tblW w:w="5000" w:type="pct"/><w:tblBorders>${border}</w:tblBorders><w:tblCellMar><w:left w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tblCellMar></w:tblPr><w:tblGrid>${"<w:gridCol/>".repeat(cols)}</w:tblGrid>${g.rows.map(tr).join("")}</w:tbl><w:p/>`;
    };
    /** One paragraph (or table, or page break); `cell` = inside a table cell (no space after; true = header row, bold). */
    const paraXml = (p: DocPara, cell?: boolean): string => {
        if (p.table) return tableXml(p.table);
        if (p.grid) return gridXml(p.grid);
        if (p.pageBreak) return "<w:p><w:r><w:br w:type=\"page\"/></w:r></w:p>";
        const attr = () => `w:id="${rev++}" w:author="${xe(reviewer)}" w:date="${date}"`;
        const numPr = p.list ? `<w:numPr><w:ilvl w:val="${p.list.level}"/><w:numId w:val="${numIdOf(p)}"/></w:numPr>` : "";
        let x = `<w:p><w:pPr><w:pStyle w:val="${p.style}"/>${numPr}${cell !== undefined ? "<w:spacing w:after=\"0\"/>" : ""}${p.align ? `<w:jc w:val="${p.align === "justify" ? "both" : p.align}"/>` : ""}${p.inserted ? `<w:rPr><w:ins ${attr()}/></w:rPr>` : ""}</w:pPr>`;
        const pcs = [p.cid, ...(p.cids ?? [])].filter((id): id is number => id !== null && id !== undefined);
        pcs.forEach((id) => { x += `<w:commentRangeStart w:id="${id}"/>`; });
        if (p.figure) x += figureRun(p.figure);
        else runsOf(p).forEach((r) => {
            const bold = r.bold || cell === true;
            if (r.start && r.ann && r.ann.cid !== null) x += `<w:commentRangeStart w:id="${r.ann.cid}"/>`;
            if (r.ann && r.ann.repl !== null) x += `<w:del ${attr()}>${tRuns(r.t, bold, "w:delText", r.f)}</w:del>`;
            else if (p.inserted) x += `<w:ins ${attr()}>${tRuns(r.t, bold, "w:t", r.f)}</w:ins>`;
            else x += r.f?.href ? linked(r.f.href, tRuns(r.t, bold, "w:t", r.f, true)) : tRuns(r.t, bold, "w:t", r.f);
            if (r.end && r.ann && r.ann.repl) x += `<w:ins ${attr()}>${tRuns(r.ann.repl, false)}</w:ins>`;
            if (r.end && r.ann && r.ann.cid !== null) x += ref(r.ann.cid);
        });
        pcs.forEach((id) => { x += ref(id); });
        return `${x}</w:p>`;
    };
    const body = m.paras.map((p) => paraXml(p)).join("");
    const lvl = (i: number, ordered: boolean) => `<w:lvl w:ilvl="${i}"><w:start w:val="1"/><w:numFmt w:val="${ordered ? ["decimal", "lowerLetter", "lowerRoman"][i % 3] : "bullet"}"/><w:lvlText w:val="${ordered ? `%${i + 1}.` : ["•", "◦", "▪"][i % 3]}"/><w:lvlJc w:val="left"/><w:pPr><w:ind w:left="${360 * (i + 1)}" w:hanging="360"/></w:pPr></w:lvl>`;
    const abstractNum = (id: number, ordered: boolean) => `<w:abstractNum w:abstractNumId="${id}"><w:multiLevelType w:val="hybridMultilevel"/>${Array.from({ length: 9 }, (_, i) => lvl(i, ordered)).join("")}</w:abstractNum>`;
    const numberingXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:numbering ${W}>${abstractNum(0, false)}${abstractNum(1, true)}<w:num w:numId="1"><w:abstractNumId w:val="0"/></w:num>`
        + `${Array.from(lists.values()).map((l) => `<w:num w:numId="${l.numId}"><w:abstractNumId w:val="${l.ordered ? 1 : 0}"/>${l.ordered ? `<w:lvlOverride w:ilvl="${l.level}"><w:startOverride w:val="1"/></w:lvlOverride>` : ""}</w:num>`).join("")}</w:numbering>`;
    const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:document ${W} xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><w:body>${body}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134" w:header="708" w:footer="708" w:gutter="0"/></w:sectPr></w:body></w:document>`;
    const commentsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:comments ${W}>${m.comments.map((c) => `<w:comment w:id="${c.id}" w:author="${xe(c.author)}" w:date="${date}" w:initials="${xe(c.initials)}">${String(c.text).split("\n").map((l) => `<w:p><w:pPr><w:pStyle w:val="CommentText"/></w:pPr><w:r><w:t xml:space="preserve">${xe(l)}</w:t></w:r></w:p>`).join("")}</w:comment>`).join("")}</w:comments>`;
    const style = (id: string, name: string, rpr: string, ppr = "") => `<w:style w:type="paragraph" w:styleId="${id}"><w:name w:val="${name}"/><w:basedOn w:val="Normal"/>${ppr ? `<w:pPr>${ppr}</w:pPr>` : ""}<w:rPr>${rpr}</w:rPr></w:style>`;
    const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:styles ${W}><w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri" w:cs="Calibri"/><w:sz w:val="22"/></w:rPr></w:rPrDefault><w:pPrDefault><w:pPr><w:spacing w:after="120" w:line="276" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults>`
        + `<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/></w:style>`
        + style("Title", "Title", '<w:b/><w:color w:val="3F4E4F"/><w:sz w:val="36"/>', '<w:spacing w:after="80"/>')
        + style("Subtitle", "Subtitle", '<w:color w:val="5C7072"/><w:sz w:val="20"/>', '<w:spacing w:after="240"/>')
        + style("Heading1", "heading 1", '<w:b/><w:color w:val="3F4E4F"/><w:sz w:val="28"/>', '<w:keepNext/><w:spacing w:before="240" w:after="120"/><w:outlineLvl w:val="0"/>')
        + style("Heading2", "heading 2", '<w:b/><w:color w:val="33494B"/><w:sz w:val="24"/>', '<w:keepNext/><w:spacing w:before="180" w:after="80"/><w:outlineLvl w:val="1"/>')
        + style("Heading3", "heading 3", '<w:b/><w:color w:val="33494B"/><w:sz w:val="22"/>', '<w:keepNext/><w:spacing w:before="160" w:after="60"/><w:outlineLvl w:val="2"/>')
        + style("Heading4", "heading 4", '<w:b/><w:i/><w:color w:val="5C7072"/><w:sz w:val="22"/>', '<w:keepNext/><w:spacing w:before="120" w:after="40"/><w:outlineLvl w:val="3"/>')
        // indents come from numbering.xml, so nested levels step in
        + style("ListBullet", "List Bullet", "", '<w:numPr><w:numId w:val="1"/></w:numPr><w:spacing w:after="60"/>')
        + style("ListNumber", "List Number", "", '<w:spacing w:after="60"/>')
        + style("Quote", "Quote", '<w:i/><w:color w:val="3F4E4F"/>', '<w:pBdr><w:left w:val="single" w:sz="18" w:space="8" w:color="D9E021"/></w:pBdr><w:ind w:left="284"/>')
        + style("Figure", "Figure", "", '<w:jc w:val="center"/><w:spacing w:before="120" w:after="240"/>')
        + style("CommentText", "annotation text", '<w:sz w:val="20"/>')
        + `<w:style w:type="character" w:styleId="CommentReference"><w:name w:val="annotation reference"/><w:rPr><w:sz w:val="16"/></w:rPr></w:style>`
        + `<w:style w:type="character" w:styleId="Hyperlink"><w:name w:val="Hyperlink"/><w:rPr><w:color w:val="1F4E79"/><w:u w:val="single"/></w:rPr></w:style></w:styles>`;
    const zip = new JSZip();
    zip.file("[Content_Types].xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Default Extension="png" ContentType="image/png"/><Default Extension="jpeg" ContentType="image/jpeg"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/><Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/><Override PartName="/word/comments.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.comments+xml"/><Override PartName="/word/numbering.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.numbering+xml"/><Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/></Types>`);
    zip.file("_rels/.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/></Relationships>`);
    zip.file("word/_rels/document.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments" Target="comments.xml"/><Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/numbering" Target="numbering.xml"/>${links.map((href, i) => `<Relationship Id="rIdLink${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" Target="${xe(href)}" TargetMode="External"/>`).join("")}${media.map((x) => `<Relationship Id="rIdImg${x.n}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/image${x.n}.${x.ext}"/>`).join("")}</Relationships>`);
    zip.file("docProps/core.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>${xe(m.title)}</dc:title>${m.docId ? `<dc:identifier>${xe(m.docId)}</dc:identifier>` : ""}<dc:creator>CRX</dc:creator><dcterms:created xsi:type="dcterms:W3CDTF">${date}</dcterms:created></cp:coreProperties>`);
    zip.file("word/document.xml", documentXml);
    zip.file("word/styles.xml", stylesXml);
    zip.file("word/comments.xml", commentsXml);
    zip.file("word/numbering.xml", numberingXml);
    media.forEach((x) => zip.file(`word/media/image${x.n}.${x.ext}`, x.base64, { base64: true }));
    return zip;
}

/** The model as a Word file to download. */
export function docxBlob(m: DocModel, reviewer: string, images: Record<string, FigureImage> = {}): Promise<Blob> {
    return docxZip(m, reviewer, images).generateAsync({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", compression: "DEFLATE" });
}

const esc = (s: string): string => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** A run's italic, underline, strike, colours and link as HTML around it (colours are checked #RRGGBB, links http(s) or mailto). */
const formatHtml = (s: string, f?: RunFormat): string => {
    if (!f) return s;
    let out = s;
    if (f.i) out = `<em>${out}</em>`;
    if (f.u) out = `<u>${out}</u>`;
    if (f.s) out = `<s>${out}</s>`;
    const css = [f.color ? `color:${f.color}` : "", f.bg ? `background-color:${f.bg}` : ""].filter(Boolean).join(";");
    if (css) out = `<span style="${css}">${out}</span>`;
    if (f.href) out = `<a href="${esc(f.href)}">${out}</a>`;
    return out;
};

/** The document's paragraphs as HTML (static: no click handlers). */
export function modelTextHtml(m: DocModel, images: Record<string, FigureImage> = {}): string {
    return paraHtml(m.paras, images);
}

function paraHtml(paras: DocPara[], images: Record<string, FigureImage>): string {
    return paras.map((p) => {
        if (p.table) return `<table class="xtbl"><tr>${p.table.columns.map((c) => `<th>${esc(c)}</th>`).join("")}</tr>${p.table.rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("")}</table>`;
        if (p.grid) return `<table class="xtbl">${p.grid.rows.map((r) => `<tr>${r.cells.map((c) => `<${r.head ? "th" : "td"}${c.span > 1 ? ` colspan="${c.span}"` : ""}>${paraHtml(c.paras, images)}</${r.head ? "th" : "td"}>`).join("")}</tr>`).join("")}</table>`;
        if (p.pageBreak) return "<hr class=\"xpb\" title=\"Page break\">";
        const img = p.figure ? images[p.figure.svgId] : null;
        const inner = p.figure ? (img ? `<img src="${img.dataUrl}" alt="${esc(p.figure.spec.title)}">` : esc(`[Picture: ${p.figure.spec.title}]`)) : runsOf(p).map((r) => {
            const t = esc(r.t).replace(/\n/g, "<br>");
            let s = formatHtml(r.bold ? `<b>${t}</b>` : t, r.f);
            if (r.ann && r.ann.repl !== null) s = `<del>${s}</del>`;
            else if (p.inserted) s = `<ins>${s}</ins>`;
            if (r.ann && r.ann.cid !== null) s = `<span class="xc" data-cid="${r.ann.cid}">${s}</span>`;
            if (r.end && r.ann && r.ann.repl) s += `<ins>${esc(r.ann.repl)}</ins>`;
            if (r.end && r.ann && r.ann.cid !== null) s += `<sup class="xn">${r.ann.cid + 1}</sup>`;
            return s;
        }).join("");
        const pcs = [p.cid, ...(p.cids ?? [])].filter((id): id is number => id !== null && id !== undefined);
        const marks = pcs.map((id) => `<span class="xc-mark" data-mk="${id}">${id + 1}</span>`).join("");
        const tag = ({ Title: "h1", Heading1: "h2", Heading2: "h3", Heading3: "h4", Heading4: "h5", Quote: "blockquote" } as Partial<Record<ParaStyle, string>>)[p.style] ?? "p";
        const isItem = p.style === "ListBullet" || p.style === "ListNumber";
        const cls = isItem ? "xli" : p.style === "Subtitle" ? "xsub" : p.style === "Figure" ? "xfig" : "";
        const css = [p.list?.level ? `padding-left:${12 + p.list.level * 18}px` : "", p.align ? `text-align:${p.align}` : ""].filter(Boolean).join(";");
        const bullet = p.style === "ListNumber" ? `${p.list?.n ?? 1}. ` : p.style === "ListBullet" ? "• " : "";
        return `<${tag}${cls ? ` class="${cls}"` : ""}${css ? ` style="${css}"` : ""}${pcs.length ? ` data-pc=" ${pcs.join(" ")} "` : ""}>${bullet}${inner || "&nbsp;"}${marks}</${tag}>`;
    }).join("");
}

/** The model as a web page, comments beside their words (prints to PDF). */
export function htmlFile(m: DocModel, today: string, images: Record<string, FigureImage> = {}): string {
    return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${esc(m.title)}</title><style>
body{font:14px/1.6 Calibri,"Segoe UI",Arial,sans-serif;color:#222;margin:0;background:#F3F6F6}
.gdoc{position:relative;display:grid;grid-template-columns:minmax(0,1fr) 290px;gap:20px;max-width:1140px;margin:24px auto;padding:0 16px;align-items:start}
.gdoc-text{background:#fff;padding:36px 40px;box-shadow:0 2px 10px rgba(0,0,0,.08);min-width:0}.gdoc-side{position:relative}
.gcard{position:absolute;left:0;right:0;background:#fff;border:1px solid #E3EAEA;border-radius:8px;padding:8px 11px;font-size:12.5px;line-height:1.45;box-shadow:0 1px 3px rgba(0,0,0,.08)}
.ga{color:#7A4D00;font-size:11px;margin-bottom:3px}.ga b{display:inline-block;min-width:18px;height:18px;line-height:18px;text-align:center;border-radius:9px;background:#FFE9B8;margin-right:4px}
h1{color:#3F4E4F;font-size:24px;margin:0 0 4px}h2{color:#3F4E4F;font-size:18px;margin:18px 0 6px}h3{font-size:15px;margin:12px 0 4px}p{margin:0 0 8px}.xsub{color:#5C7072}.xfig{text-align:center}.xfig img{max-width:100%;height:auto}.xli{padding-left:12px}
.xc{background:#FFF2C2;border-bottom:2px solid #E0A43A}.xn{color:#9E5C00;font-weight:700;font-size:10px}del{color:#C1272D;text-decoration:line-through}ins{color:#1E7A34;text-decoration:underline}
.xc-mark{display:inline-block;min-width:16px;padding:0 4px;margin-left:4px;border-radius:8px;background:#FFE9B8;color:#7A4D00;font-size:10px;font-weight:700;text-align:center}
[data-pc]{box-shadow:inset 3px 0 #F2D9A6;padding-left:8px}.foot{color:#888;font-size:11px;margin-top:24px}
h4{font-size:14px;margin:10px 0 4px}h5{font-size:13px;font-style:italic;margin:8px 0 4px}blockquote{margin:0 0 8px;padding:2px 12px;border-left:3px solid #D9E021;font-style:italic}a{color:#1F4E79}
.xtbl{width:100%;border-collapse:collapse;margin:0 0 10px}.xtbl th,.xtbl td{border:1px solid #C9D2D3;padding:4px 8px;text-align:left;vertical-align:top}.xtbl th{background:#F3F5C4}.xtbl p{margin:0}.xpb{border:0;border-top:2px dashed #C9D2D3;margin:18px 0}
@media(max-width:760px){.gdoc{grid-template-columns:minmax(0,1fr)}.gcard{position:static;margin-bottom:8px}.gdoc-side{height:auto!important}}
@media print{body{background:#fff}.gdoc-text{box-shadow:none}}</style></head><body><div class="gdoc" id="gdoc"><div class="gdoc-text">${modelTextHtml(m, images)}<p class="foot">Exported from CRX on ${esc(fmt(today))}. Comments sit beside their words; red = suggested deletion, green = suggested insertion.</p></div><div class="gdoc-side">${m.comments.map((c) => `<div class="gcard" data-cid="${c.id}"><div class="ga"><b>${c.id + 1}</b>${esc(c.author)}</div>${esc(c.text).replace(/\n/g, "<br>")}</div>`).join("")}</div></div>
<script>function lay(){var w=document.getElementById("gdoc"),s=w.querySelector(".gdoc-side");if(innerWidth<760){s.style.height="auto";return}var last=-1e9,b=0;[].slice.call(s.children).map(function(c){var id=c.getAttribute("data-cid"),a=w.querySelector('.gdoc-text [data-cid="'+id+'"]')||w.querySelector('.gdoc-text [data-pc~="'+id+'"]');return{c:c,t:a?a.offsetTop-s.offsetTop-4:0}}).sort(function(x,y){return x.t-y.t}).forEach(function(x){var t=Math.max(x.t,last+8,0);x.c.style.top=t+"px";last=t+x.c.offsetHeight;b=Math.max(b,last)});s.style.height=b+"px"}addEventListener("load",lay);addEventListener("resize",lay);lay();${"<"}/script></body></html>`;
}

/** A safe file name ("Aurora RFP — comments for Aurora — 20 Oct 2026.docx"). */
export const fileName = (base: string, today: string, ext: string): string =>
    `${base.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim()} — ${fmt(today)}.${ext}`;
